import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clinicData } from '../data';
import ServicesSection from '../components/ServicesSection';
import { Helmet } from 'react-helmet-async';

const Treatments = () => {
  const handleWhatsAppCall = () => {
    window.open(`https://wa.me/91${clinicData.contact.whatsappNumber}?text=Hello, I would like to know more about your treatments`, '_blank');
  };

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>Treatments - {clinicData.name} | Men's Health & Andrology</title>
        <meta name="description" content={clinicData.seo.treatments.description} />
        <link rel="canonical" href="https://www.manclinics.com/treatments" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-pink-50">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-block bg-[#1E5A8E]/10 text-[#1E5A8E] px-4 py-2 rounded-full text-sm font-medium mb-6">
            Our Services
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive <span className="text-[#D91656]">Men's Health</span> Treatments
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Advanced medical solutions for urology, andrology, and men's sexual health. Every treatment is personalized, confidential, and backed by the latest medical research.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 px-4 bg-white">
        <ServicesSection services={clinicData.services} title="Complete Treatment Solutions" subtitle="Advanced andrology, urology, and men's health treatments tailored to your needs" className="py-24" />
      </section>

      {/* Special Technologies */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
            Special <span className="text-[#D91656]">Technologies</span>
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            First center in Andhra Pradesh with advanced treatment technologies
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {clinicData.specialTechnologies.map((tech, index) => {
              const IconComponent = index === 0 ? LucideIcons.Zap : LucideIcons.Shield;
              return (
                <Card key={index} className="border-2 border-[#D91656]/20 hover:border-[#D91656] transition-colors hover:shadow-xl group">
                  <CardContent className="p-8 text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#D91656] to-[#B01348] rounded-full group-hover:scale-110 transition-transform">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{tech.name}</h3>
                    <p className="text-gray-600">{tech.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* BPH Procedures */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Procedures for BPH <span className="text-[#D91656]">(Prostate Growth)</span>
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Minimally Invasive Surgical Therapies (MIST) and advanced laser procedures
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clinicData.bphProcedures.map((procedure, index) => (
              <Card key={index} className="border-l-4 border-[#1E5A8E] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900">{procedure.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Prosthetic Urology */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Prosthetic <span className="text-[#D91656]">Urology</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {clinicData.prostheticUrology.map((procedure, index) => (
              <Card key={index} className="border-l-4 border-[#D91656] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 text-lg">{procedure.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Allied Services */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our <span className="text-[#D91656]">Allied Services</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {clinicData.alliedServices.map((service, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-[#D91656]/10 rounded-full flex items-center justify-center">
                    <LucideIcons.CheckCircle className="h-5 w-5 text-[#D91656]" />
                  </div>
                </div>
                <p className="text-gray-700 font-medium">{service.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Treatment */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Choose Our <span className="text-[#D91656]">Treatment Approach</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D91656]/10 rounded-full mb-4">
                <LucideIcons.ClipboardCheck className="h-8 w-8 text-[#D91656]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Evaluation</h3>
              <p className="text-gray-600">Thorough diagnosis using advanced medical technology</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E5A8E]/10 rounded-full mb-4">
                <LucideIcons.Target className="h-8 w-8 text-[#1E5A8E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalized Plans</h3>
              <p className="text-gray-600">Custom treatment tailored to your specific needs</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D91656]/10 rounded-full mb-4">
                <LucideIcons.Shield className="h-8 w-8 text-[#D91656]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Safe & Proven</h3>
              <p className="text-gray-600">Evidence-based treatments with proven results</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E5A8E]/10 rounded-full mb-4">
                <LucideIcons.Users className="h-8 w-8 text-[#1E5A8E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ongoing Support</h3>
              <p className="text-gray-600">Continuous care and follow-up throughout recovery</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1E5A8E] to-[#D91656]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Treatment Journey?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Consult with our specialists to discuss the best treatment options for your condition
          </p>
          <Button
            onClick={handleWhatsAppCall}
            size="lg"
            className="bg-white text-[#D91656] hover:bg-gray-100 text-lg px-8 py-6"
          >
            Book Your Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Treatments;

